/* BMN Quote Brain v1
 *
 * Channel-agnostic core for the Brand Me Now quote assistant.
 * No DOM or UI dependencies. Reusable for the web widget today
 * and for the future voice agent / visual designer.
 *
 * Exports (attaches to window.BMNQuoteBrain in the browser, or
 * module.exports under Node):
 *   parse(text, config)         -> extracted signals from a user turn
 *   mergeState(prev, next)      -> combine signals into cumulative state
 *   nextQuestion(state, config) -> the next slot to fill (or null when ready to gate)
 *   mapGarments(state, config)  -> matched garment records from config
 *   priceBlanks(matched)        -> display-ready blank "from" price rows
 *   buildLeadPayload(state,
 *                    contact,
 *                    matched)   -> the lead payload per spec section 6
 *   formatLeadEmailBody(payload)-> plain-text summary for the contact form
 *   validateContact(contact)    -> { ok, errors }
 *   consts                      -> shared constants
 *
 * Rules (from spec):
 *  - Prices are ex VAT, labelled "from, ex VAT".
 *  - Turnaround is quoted as "7-10 working days" (never invent).
 *  - Never invent decorated / all-in prices. Never expose trade costs.
 *  - Plain UK English, warm and brief. No em dashes.
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else {
    root.BMNQuoteBrain = api;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var TURNAROUND = '7-10 working days';
  var CURRENCY = 'GBP';
  var CONTACT_EMAIL = 'info@brandmenow.co.uk';
  var CONTACT_PHONE = '0121 472 5300';
  var LEAD_TAG = 'bmn-quote-lead';

  var METHOD_LABELS = {
    embroidery: 'embroidery',
    dtf: 'DTF print',
    'screen-print': 'screen print',
    vinyl: 'heat transfer vinyl',
    dtg: 'DTG print',
    sublimation: 'sublimation'
  };

  function normalise(s) {
    return String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function unique(arr) {
    var seen = {};
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      if (!seen[arr[i]]) { seen[arr[i]] = 1; out.push(arr[i]); }
    }
    return out;
  }

  // Parse a single free-text turn into structured signals.
  function parse(text, config) {
    var t = normalise(text);
    var out = {
      rawText: String(text || ''),
      garments: [],
      methods: [],
      positions: [],
      sector: '',
      isReorder: false,
      qty: null,
      colour: null
    };

    if (/\b(reorder|repeat order|same as last time|previous order)\b/.test(t)) {
      out.isReorder = true;
    }

    // Quantity. Prefer explicit unit words; fall back to a bare number.
    var qm = t.match(/(\d{1,4})\s*(pcs?|pieces?|units?|off|x|shirts?|polos?|hoodies?|caps?|tees?|garments?|jumpers?|sweats?|softshells?)/);
    if (!qm) qm = t.match(/(?:^|\s)(\d{2,4})(?=\s|$)/);
    if (qm) {
      var n = parseInt(qm[1], 10);
      if (!isNaN(n) && n > 0 && n < 10000) out.qty = n;
    }

    // Decoration methods.
    if (/\b(embroider(y|ed)?|stitch(ed)?)\b/.test(t)) out.methods.push('embroidery');
    if (/\b(dtf|direct[- ]to[- ]film|transfer print)\b/.test(t)) out.methods.push('dtf');
    if (/\bscreen[- ]?print(ing)?\b/.test(t)) out.methods.push('screen-print');
    if (/\b(vinyl|htv|heat[- ]transfer[- ]vinyl)\b/.test(t)) out.methods.push('vinyl');
    if (/\b(dtg|direct[- ]to[- ]garment)\b/.test(t)) out.methods.push('dtg');
    if (/\bsublimat(ion|ed)\b/.test(t)) out.methods.push('sublimation');

    // Positions.
    if (/\bleft[- ]?(chest|breast)\b|\blc\b/.test(t)) out.positions.push('left chest');
    if (/\bright[- ]?(chest|breast)\b|\brc\b/.test(t)) out.positions.push('right chest');
    if (/\b(full |large )?back(\s*print)?\b/.test(t)) out.positions.push('back');
    if (/\bfront\b/.test(t) && !/left chest|right chest/.test(t)) out.positions.push('front');
    if (/\bsleeves?\b/.test(t)) out.positions.push('sleeve');
    if (/\bcap[- ]?front|front[- ]?(of[- ]?)?cap\b/.test(t)) out.positions.push('cap front');

    // Colour (common set). Kept loose; team confirms exact swatch.
    var colours = ['black', 'white', 'navy', 'royal', 'red', 'grey', 'gray', 'charcoal',
                   'burgundy', 'bottle green', 'forest', 'kelly', 'lime', 'yellow',
                   'orange', 'purple', 'pink', 'sky', 'blue', 'green'];
    for (var c = 0; c < colours.length; c++) {
      if (new RegExp('\\b' + colours[c] + '\\b').test(t)) { out.colour = colours[c]; break; }
    }

    // Sector hints. Optional slot only, never blocks the gate.
    var sectorMap = [
      { rx: /\b(pub|bar|restaurant|hospitality|cafe|café)\b/, val: 'hospitality' },
      { rx: /\b(school|academy|college|university|uni|nursery)\b/, val: 'education' },
      { rx: /\b(clinic|surgery|nhs|dental|healthcare|hospital|care home)\b/, val: 'healthcare' },
      { rx: /\b(builder|construction|scaffolder|site team|hi[- ]vis)\b/, val: 'construction' },
      { rx: /\b(charity|fundraiser|nonprofit)\b/, val: 'charity' },
      { rx: /\b(gym|fitness|sport(s)?|club|team)\b/, val: 'sport' },
      { rx: /\b(office|corporate|company|business|startup|agency)\b/, val: 'corporate' }
    ];
    for (var s = 0; s < sectorMap.length; s++) {
      if (sectorMap[s].rx.test(t)) { out.sector = sectorMap[s].val; break; }
    }

    // Garments. First try base code + product name literal hits.
    var garments = (config && config.garments) || [];
    for (var g = 0; g < garments.length; g++) {
      var G = garments[g];
      if (new RegExp('\\b' + G.baseCode.toLowerCase() + '\\b').test(t)) {
        out.garments.push({ baseCode: G.baseCode });
        continue;
      }
      // Loose name hit (short tokens from the product name).
      var nameToken = G.name.toLowerCase().replace(/[^a-z]+/g, ' ').trim();
      if (nameToken && t.indexOf(nameToken) !== -1) {
        out.garments.push({ baseCode: G.baseCode });
      }
    }

    // If nothing hit, fall back to garment TYPE keywords.
    if (out.garments.length === 0 && garments.length) {
      var typeRules = [
        { rx: /\blong[- ]?sleeve(s|d)?\b/, type: 'T-Shirts', hint: 'long-sleeve' },
        { rx: /\bv[- ]?neck(s)?\b/,        type: 'T-Shirts', hint: 'v-neck' },
        { rx: /\btank[s]?(\s*top)?\b|\bvest(s)?\b/, type: 'Vests' },
        { rx: /\bpolo(s|\s*shirt(s)?)?\b/, type: 'Polos' },
        { rx: /\bt[- ]?shirt(s)?\b|\btees?\b/, type: 'T-Shirts' },
        { rx: /\bhood(y|ie)s?\b/,          type: 'Hoodies' },
        { rx: /\b(sweat(shirt)?s?|jumper(s)?)\b/, type: 'Sweatshirts' },
        { rx: /\bsoftshell(s)?\b|\bjacket(s)?\b/, type: 'Softshells' },
        { rx: /\bcaps?\b|\bhats?\b/,        type: 'Caps' }
      ];
      for (var r = 0; r < typeRules.length; r++) {
        var rule = typeRules[r];
        if (!rule.rx.test(t)) continue;
        var pool = garments.filter(function (x) { return x.productType === rule.type; });
        if (!pool.length) continue;
        var pick = null;
        if (rule.hint === 'long-sleeve') pick = pool.find(function (x) { return /long-sleeve/i.test(x.name); });
        else if (rule.hint === 'v-neck') pick = pool.find(function (x) { return /v-neck/i.test(x.name); });
        if (!pick) pick = pool[0];
        out.garments.push({ baseCode: pick.baseCode });
        break;
      }
    }

    out.methods = unique(out.methods);
    out.positions = unique(out.positions);
    return out;
  }

  // Combine a fresh parse into cumulative state.
  function mergeState(prev, next) {
    prev = prev || {};
    next = next || {};
    var merged = {
      rawText: prev.rawText || '',
      garments: (prev.garments || []).slice(),
      methods: (prev.methods || []).slice(),
      positions: (prev.positions || []).slice(),
      sector: prev.sector || '',
      isReorder: !!prev.isReorder,
      qty: prev.qty || null,
      colour: prev.colour || null,
      sizes: prev.sizes || {}
    };
    if (next.rawText) merged.rawText = (merged.rawText ? merged.rawText + ' | ' : '') + next.rawText;
    if (next.qty && !merged.qty) merged.qty = next.qty;
    if (next.colour && !merged.colour) merged.colour = next.colour;
    if (next.sector && !merged.sector) merged.sector = next.sector;
    if (next.isReorder) merged.isReorder = true;
    merged.methods = unique(merged.methods.concat(next.methods || []));
    merged.positions = unique(merged.positions.concat(next.positions || []));
    var seen = {};
    for (var i = 0; i < merged.garments.length; i++) seen[merged.garments[i].baseCode] = 1;
    for (var j = 0; j < (next.garments || []).length; j++) {
      var bc = next.garments[j].baseCode;
      if (!seen[bc]) { merged.garments.push({ baseCode: bc }); seen[bc] = 1; }
    }
    return merged;
  }

  // Decide the next question. Returns null when we have enough to gate.
  function nextQuestion(state, config) {
    state = state || {};
    if (!state.garments || state.garments.length === 0) {
      var eg = ['polo', 't-shirt', 'hoodie', 'sweatshirt', 'softshell', 'cap'];
      return {
        field: 'garment',
        prompt: 'Happy to help. What garment are you after? For example: ' + eg.join(', ') + '.'
      };
    }
    if (!state.qty) {
      return {
        field: 'qty',
        prompt: 'Roughly how many do you need?'
      };
    }
    if (!state.methods || state.methods.length === 0) {
      return {
        field: 'method',
        prompt: 'How would you like it branded? Embroidery, DTF print, or something else?'
      };
    }
    if (!state.positions || state.positions.length === 0) {
      return {
        field: 'positions',
        prompt: 'Where would you like the branding? For example: left chest, back, sleeve.'
      };
    }
    return null;
  }

  function mapGarments(state, config) {
    var garments = (config && config.garments) || [];
    var out = [];
    var codes = (state && state.garments) || [];
    for (var i = 0; i < codes.length; i++) {
      var bc = codes[i].baseCode;
      var found = null;
      for (var k = 0; k < garments.length; k++) {
        if (garments[k].baseCode === bc) { found = garments[k]; break; }
      }
      if (found) out.push(found);
    }
    return out;
  }

  function priceBlanks(matched) {
    var out = [];
    for (var i = 0; i < (matched || []).length; i++) {
      var g = matched[i];
      out.push({
        baseCode: g.baseCode,
        sku: g.skuPrefix,
        name: g.name,
        vendor: g.vendor,
        displayFrom: g.blankFrom,
        currency: CURRENCY,
        note: 'from, ex VAT. Decoration quoted separately.'
      });
    }
    return out;
  }

  function validateContact(contact) {
    contact = contact || {};
    var errors = {};
    var name = (contact.name || '').trim();
    var email = (contact.email || '').trim();
    if (!name || name.length < 2) errors.name = 'Please pop your name in.';
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.email = 'Please add a valid email so we can send the itemised quote.';
    return { ok: Object.keys(errors).length === 0, errors: errors };
  }

  function buildLeadPayload(state, contact, matched) {
    state = state || {};
    contact = contact || {};
    matched = matched || [];
    var garments = [];
    for (var i = 0; i < matched.length; i++) {
      garments.push({
        sku: matched[i].baseCode,
        qty: i === 0 ? (state.qty || 0) : 0,
        colour: state.colour || '',
        sizes: state.sizes || {}
      });
    }
    var blankShown = [];
    for (var j = 0; j < matched.length; j++) {
      blankShown.push({ sku: matched[j].baseCode, displayFrom: matched[j].blankFrom });
    }
    return {
      name: (contact.name || '').trim(),
      email: (contact.email || '').trim(),
      company: (contact.company || '').trim(),
      phone: (contact.phone || '').trim(),
      enquiry: {
        rawText: state.rawText || '',
        garments: garments,
        methods: state.methods || [],
        positions: state.positions || [],
        sector: state.sector || '',
        isReorder: !!state.isReorder
      },
      blankPricesShown: blankShown,
      turnaround: TURNAROUND,
      timestamp: new Date().toISOString(),
      source: 'quote-assistant-v1',
      tag: LEAD_TAG
    };
  }

  // Human-readable summary for the Shopify contact form message body.
  function formatLeadEmailBody(p) {
    p = p || {};
    var lines = [];
    lines.push('BMN Quote Assistant lead');
    lines.push('Received: ' + p.timestamp);
    lines.push('Source: ' + (p.source || 'quote-assistant'));
    lines.push('');
    lines.push('Contact');
    lines.push('  Name:    ' + p.name);
    lines.push('  Email:   ' + p.email);
    if (p.company) lines.push('  Company: ' + p.company);
    if (p.phone)   lines.push('  Phone:   ' + p.phone);
    lines.push('');
    lines.push('Enquiry');
    var e = p.enquiry || {};
    for (var i = 0; i < (e.garments || []).length; i++) {
      var g = e.garments[i];
      lines.push('  Garment ' + (i + 1) + ': ' + g.sku +
                 (g.qty ? '  x' + g.qty : '') +
                 (g.colour ? '  colour: ' + g.colour : ''));
    }
    if ((e.methods || []).length) {
      var readable = e.methods.map(function (m) { return METHOD_LABELS[m] || m; });
      lines.push('  Method(s): ' + readable.join(', '));
    }
    if ((e.positions || []).length) lines.push('  Position(s): ' + e.positions.join(', '));
    if (e.sector)    lines.push('  Sector: ' + e.sector);
    if (e.isReorder) lines.push('  Reorder: yes');
    if (e.rawText)   lines.push('  Original message: ' + e.rawText);
    lines.push('');
    lines.push('Blank "from" prices shown to customer (ex VAT)');
    for (var k = 0; k < (p.blankPricesShown || []).length; k++) {
      var b = p.blankPricesShown[k];
      lines.push('  ' + b.sku + ': £' + Number(b.displayFrom).toFixed(2));
    }
    lines.push('');
    lines.push('Turnaround quoted: ' + p.turnaround);
    lines.push('Tag: ' + p.tag);
    return lines.join('\n');
  }

  return {
    parse: parse,
    mergeState: mergeState,
    nextQuestion: nextQuestion,
    mapGarments: mapGarments,
    priceBlanks: priceBlanks,
    buildLeadPayload: buildLeadPayload,
    formatLeadEmailBody: formatLeadEmailBody,
    validateContact: validateContact,
    consts: {
      TURNAROUND: TURNAROUND,
      CURRENCY: CURRENCY,
      CONTACT_EMAIL: CONTACT_EMAIL,
      CONTACT_PHONE: CONTACT_PHONE,
      LEAD_TAG: LEAD_TAG,
      METHOD_LABELS: METHOD_LABELS
    }
  };
});
